/**
 * Svelte action — balayage de ligne à deux paliers, dans les deux sens
 * (le « long swipe » d'Apple Mail).
 *
 *   35% < |dx| < 65% → palier COURT
 *   |dx| > 65%       → palier LONG, le fond révélé change
 *
 * L'action ne connaît que des POSITIONS, jamais des actions métier : c'est
 * l'appelant qui décide ce que déclenche « long à gauche », d'après les
 * réglages de l'utilisateur (`lib/settings.svelte.ts`). Sans cette
 * séparation, rendre les gestes configurables demandait de câbler les
 * réglages jusque dans la mécanique du doigt.
 *
 * Algo :
 *  - touchstart n'importe où sur la row
 *  - touchmove suit le doigt → translateX
 *  - lock directionnel : si dy domine au début, abort (pour ne pas
 *    hijack le scroll vertical de la liste)
 *  - touchend déclenche l'action selon |dx| et la direction
 *
 * Visuellement : le data-swipe-direction sur le frame révèle le bon
 * background. À gauche, le bg switch entre archive (small) et later (long)
 * via un data-swipe-stage="archive"|"later" supplémentaire.
 *
 * Usage :
 *   <li use:swipeRow={{ onArchive, onLater, onToggleRead, isUnread }}>
 *     ...
 *   </li>
 */

import { haptic } from "./haptic";

export interface SwipeRowOptions {
  onShortLeft: () => void;
  onLongLeft: () => void;
  onShortRight: () => void;
  onLongRight: () => void;
  /**
   * Par position : l'action retire-t-elle la ligne de la liste ?
   * Archiver la fait sortir, « lu / non lu » la laisse en place — et une
   * ligne qui sort de l'écran pour revenir aussitôt est un clignotement.
   */
  exits: {
    shortLeft: boolean;
    longLeft: boolean;
    shortRight: boolean;
    longRight: boolean;
  };
  /** Appui long sans mouvement — annule le balayage en cours. */
  onLongPress?: () => void;
  enabled?: boolean;
}

const SHORT_THRESHOLD_RATIO = 0.35; // franchissement du palier court
const LONG_THRESHOLD_RATIO  = 0.65; // franchissement du palier long
const SLIDE_OUT_MS = 180;
const SNAP_BACK_MS = 180;
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
const DIRECTION_LOCK_MIN = 8; // px
const LONG_PRESS_MS = 480;
/** Au-delà, le doigt bouge : c'est un balayage, plus un appui. */
const LONG_PRESS_SLOP = 10; // px

/**
 * Cherche la row child à transformer ET le wrapper d'actions à révéler.
 * Convention : `node` est le `<li>` ; il contient un `[data-row-content]`
 * qui translate, et deux `[data-row-action="left|right"]` qui restent en
 * place dessous.
 */
function findContent(node: HTMLElement): HTMLElement | null {
  return node.querySelector<HTMLElement>("[data-row-content]");
}

export function swipeRow(node: HTMLElement, options: SwipeRowOptions) {
  let opts = options;
  let startX = 0;
  let startY = 0;
  let active = false;
  let aborted = false;
  let currentDelta = 0;
  let direction: "left" | "right" | null = null;
  let longPressTimer: number | null = null;

  const content = findContent(node);
  if (!content) {
    console.warn("swipeRow: no [data-row-content] found in", node);
  }

  function setTransform(x: number) {
    if (!content) return;
    content.style.transform = x === 0 ? "" : `translateX(${x}px)`;
  }

  function setActionVisibility(d: "left" | "right" | null, stage?: "short" | "long") {
    // Le CSS lit `data-swipe-direction` pour révéler le bon fond, et
    // `data-swipe-stage` pour basculer court → long.
    if (d) node.setAttribute("data-swipe-direction", d);
    else node.removeAttribute("data-swipe-direction");
    if (stage) node.setAttribute("data-swipe-stage", stage);
    else node.removeAttribute("data-swipe-stage");
  }

  function animateTo(x: number, ms: number, then?: () => void) {
    if (!content) {
      then?.();
      return;
    }
    content.style.transition = `transform ${ms}ms ${EASING}`;
    setTransform(x);
    window.setTimeout(() => {
      if (content) content.style.transition = "";
      then?.();
    }, ms);
  }

  function reset(animate: boolean) {
    if (animate) {
      animateTo(0, SNAP_BACK_MS, () => setActionVisibility(null));
    } else {
      setTransform(0);
      setActionVisibility(null);
    }
    direction = null;
  }

  function cancelLongPress() {
    if (longPressTimer !== null) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function onTouchStart(e: TouchEvent) {
    if (opts.enabled === false) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    startX = t.clientX;
    startY = t.clientY;
    active = true;
    aborted = false;
    currentDelta = 0;
    direction = null;
    if (content) content.style.transition = "";

    cancelLongPress();
    if (opts.onLongPress) {
      longPressTimer = window.setTimeout(() => {
        longPressTimer = null;
        // Le doigt n'a pas bougé : ce n'était pas un balayage.
        aborted = true;
        active = false;
        reset(true);
        haptic("heavy");
        opts.onLongPress?.();
      }, LONG_PRESS_MS);
    }
  }

  function onTouchMove(e: TouchEvent) {
    if (!active || aborted) return;
    const t = e.touches[0];
    const dx = t.clientX - startX;
    const dy = Math.abs(t.clientY - startY);

    if (Math.abs(dx) > LONG_PRESS_SLOP || dy > LONG_PRESS_SLOP) cancelLongPress();

    // Direction lock : avant qu'on ait bougé latéralement de DIRECTION_LOCK_MIN,
    // si on bouge verticalement plus, on lâche.
    if (Math.abs(dx) < DIRECTION_LOCK_MIN && dy > DIRECTION_LOCK_MIN) {
      aborted = true;
      reset(true);
      return;
    }

    currentDelta = dx;
    direction = dx < 0 ? "left" : dx > 0 ? "right" : null;

    // Haptique au franchissement du palier long, pour que le doigt sente
    // qu'il vient de changer d'action sans avoir à regarder.
    if (direction) {
      const w = window.innerWidth;
      const isLong = Math.abs(dx) > w * LONG_THRESHOLD_RATIO;
      const wasLong = node.getAttribute("data-swipe-stage") === "long";
      setActionVisibility(direction, isLong ? "long" : "short");
      if (isLong && !wasLong) haptic("light");
    } else {
      setActionVisibility(null);
    }
    setTransform(dx);

    // preventDefault pour empêcher le scroll horizontal natif
    if (e.cancelable && Math.abs(dx) > DIRECTION_LOCK_MIN) e.preventDefault();
  }

  function onTouchEnd() {
    cancelLongPress();
    if (!active) return;
    active = false;
    if (aborted) return;

    const w = window.innerWidth;

    const dxAbs = Math.abs(currentDelta);
    if (dxAbs < w * SHORT_THRESHOLD_RATIO) {
      reset(true);
      return;
    }

    const long = dxAbs >= w * LONG_THRESHOLD_RATIO;
    const left = currentDelta < 0;
    const pos = left
      ? (long ? "longLeft" : "shortLeft")
      : (long ? "longRight" : "shortRight");
    const run = {
      shortLeft: opts.onShortLeft,
      longLeft: opts.onLongLeft,
      shortRight: opts.onShortRight,
      longRight: opts.onLongRight,
    }[pos];

    haptic("medium");
    if (opts.exits[pos]) {
      // La ligne sort par le côté vers lequel le doigt allait.
      animateTo(left ? -w : w, SLIDE_OUT_MS, () => {
        run();
        setTransform(0);
        setActionVisibility(null);
      });
    } else {
      run();
      reset(true);
    }
  }

  node.addEventListener("touchstart", onTouchStart, { passive: true });
  node.addEventListener("touchmove", onTouchMove, { passive: false });
  node.addEventListener("touchend", onTouchEnd, { passive: true });
  node.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return {
    update(newOpts: SwipeRowOptions) {
      opts = newOpts;
    },
    destroy() {
      cancelLongPress();
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    },
  };
}
